/**
 * Indian Mobile Number Validation Utilities
 */

export const INDIAN_MOBILE_REGEX = /^[6-9][0-9]{9}$/;

/**
 * Normalize Indian mobile number input
 * - Trim whitespace
 * - Remove spaces, hyphens, parentheses
 * - Remove +91 or 91 prefix if present
 * - Keep digits only
 * 
 * @param {string} input - Raw input string
 * @returns {string} - Normalized 10-digit number or empty string
 */
export function normalizeIndianMobile(input) {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  // Trim and remove all non-digit characters except + at the start
  let normalized = input.trim().replace(/[\s\-()]/g, '');
  
  // Remove +91 or 91 prefix
  if (normalized.startsWith('+91')) {
    normalized = normalized.substring(3);
  } else if (normalized.startsWith('91') && normalized.length > 10) {
    normalized = normalized.substring(2);
  }
  
  // Keep only digits
  normalized = normalized.replace(/\D/g, '');
  
  // Limit to 10 digits
  return normalized.slice(0, 10);
}

/**
 * Check if input is a valid Indian mobile number
 * 
 * @param {string} input - Mobile number to validate
 * @returns {boolean} - True if valid 10-digit Indian mobile number
 */
export function isValidIndianMobile(input) {
  if (!input || typeof input !== 'string') {
    return false;
  }
  
  const normalized = normalizeIndianMobile(input);
  return INDIAN_MOBILE_REGEX.test(normalized);
}

/**
 * Get validation error message for Indian mobile number
 * 
 * @param {string} input - Mobile number to validate
 * @param {boolean} required - Whether field is required (default: true)
 * @returns {string|null} - Error message or null if valid
 */
export function getIndianMobileError(input, required = true) {
  if (!input || !input.trim()) {
    if (required) {
      return 'Mobile number is required';
    }
    return null; // Not required and empty is valid
  }
  
  if (!isValidIndianMobile(input)) {
    return 'Enter a valid 10-digit Indian mobile number';
  }
  
  return null; // Valid
}

